<script setup>
import { computed } from 'vue'
import { cssGradient } from '@/utils/monitoring/colormaps'
import MetricChart from './MetricChart.vue'

const props = defineProps({
  title: { type: String, required: true },
  value: { type: String, required: true },
  unit: { type: String, default: '' },
  color: { type: String, required: true },
  points: { type: Array, required: true },
  xMin: { type: Number, required: true },
  xMax: { type: Number, required: true },
  mapLegend: { type: Object, default: null },
  rangeText: { type: String, default: '' },
  variants: { type: Array, default: null },
  activeVariant: { type: String, default: '' },
  scrubDate: { type: String, default: null },
  expanded: { type: Boolean, default: false },
  // [lo, hi] the raster is actually stretched to, when known
  domain: { type: Array, default: null },
})

const emit = defineEmits(['toggle', 'variant', 'scrub'])

const series = computed(() => [
  {
    label: props.title,
    points: props.points,
    color: props.color,
    gradient: props.mapLegend ? { stops: props.mapLegend.stops } : undefined,
  },
])

// The legend describes the raster, so its own frozen domain wins over the
// nominal metric range when the backend reports one.
const legendBounds = computed(() => {
  const d = props.domain
  if (!d || d.length !== 2 || !Number.isFinite(d[0]) || !Number.isFinite(d[1])) {
    return { low: props.mapLegend?.low, high: props.mapLegend?.high }
  }
  const dp = Math.abs(d[1] - d[0]) < 10 ? 1 : 0
  return { low: d[0].toFixed(dp), high: d[1].toFixed(dp) }
})
</script>

<template>
  <button
    class="text-left rounded-lg border bg-card p-3 hover:border-primary/50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    :class="expanded ? 'col-span-2' : ''"
    @click="emit('toggle')"
  >
    <div class="flex items-center gap-1.5">
      <span class="text-sm font-medium text-muted-foreground truncate">{{ title }}</span>
    </div>

    <div class="mt-1 flex items-baseline gap-1.5 flex-wrap">
      <span class="text-xl font-bold leading-none">{{ value }}</span>
      <span v-if="unit" class="text-xs text-muted-foreground">{{ unit }}</span>
    </div>

    <!-- bottom border = baseline, so data-less stretches still read as a timeline -->
    <div v-if="!expanded" class="mt-1 h-16 border-b border-border/40">
      <MetricChart :series="series" :x-min="xMin" :x-max="xMax" sparkline />
    </div>

    <template v-else>
      <div
        v-if="variants && variants.length > 1"
        class="mt-2 flex w-fit gap-0.5 rounded-lg bg-muted p-0.5"
      >
        <button
          v-for="v in variants"
          :key="v.key"
          class="rounded-md px-2 py-0.5 text-xs font-medium transition-colors"
          :class="activeVariant === v.key ? 'bg-background shadow font-semibold' : 'text-muted-foreground hover:text-foreground'"
          @click.stop="emit('variant', v.key)"
        >
          {{ v.label }}
        </button>
      </div>
      <div class="mt-3 h-40">
        <MetricChart
          :series="series"
          :x-min="xMin"
          :x-max="xMax"
          :unit="unit"
          :marker="scrubDate ? Date.parse(scrubDate) : null"
          @hover-date="(d) => emit('scrub', d)"
        />
      </div>
      <div v-if="rangeText" class="mt-2 text-xs text-muted-foreground">
        Data available: {{ rangeText }}
      </div>
      <div v-if="mapLegend" class="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
        <span class="shrink-0">Map</span>
        <span>{{ legendBounds.low }}</span>
        <div
          class="h-2 flex-1 rounded-full"
          :style="{ background: cssGradient(mapLegend.stops) }"
        ></div>
        <span>{{ legendBounds.high }}</span>
      </div>
    </template>
  </button>
</template>
